import React, { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import ShayariCard from '../components/ShayariCard';
import Sidebar from '../components/Sidebar';
import CategoryStrip from '../components/CategoryStrip';
import AdBanner from '../components/AdBanner';
import { CATEGORIES, SHAYARI } from '../data/shayari';
import { useApp } from '../context/AppContext';
import useSEO from '../hooks/useSEO';
import Logo from '../components/Logo';
import GlobalSearch from '../components/GlobalSearch';
import FAQAccordion from '../components/FAQAccordion';

export const CATEGORY_DESCRIPTIONS = {
  love:         { hi: 'मोहब्बत की शायरी दिल की गहराइयों से निकलती है। यहाँ पाइए ग़ालिब, फ़ैज़, अहमद फ़राज़ और परवीन शाकिर की बेहतरीन इश्क़ व मोहब्बत की शायरियाँ हिंदी, उर्दू और अंग्रेज़ी में। ये शायरियाँ सालगिरह, प्रपोज़ करने, या बस अपने साथी को यह जताने के लिए उपयुक्त हैं कि आप उन्हें कितना चाहते हैं — क्लासिक ग़ज़ल के गहरे अंदाज़ से लेकर आज के छोटे, सीधे शेर तक।', en: "Dive into the deepest expressions of love through hand-picked verses by Ghalib, Faiz Ahmed Faiz, Ahmad Faraz, and Parveen Shakir. This collection suits anniversaries, proposals, or simply telling someone how much they mean to you — ranging from the layered metaphors of classical ghazal to short, direct modern couplets." },
  sad:          { hi: 'दर्द की शायरी वो होती है जो दिल की बात कहती है। ग़ालिब की बेकरारी, फ़ैज़ का इंतज़ार, जौन एलिया का दर्द — इस संग्रह में बेहतरीन उदासी और तन्हाई की शायरियाँ मिलेंगी। जब शब्द कम पड़ जाएँ और सिर्फ़ कोई समझने वाला चाहिए हो, तब ये शायरियाँ काम आती हैं — भारी दिल के लिए, अकेले पढ़ने के लिए या किसी दोस्त को यह जताने के लिए कि आप उसका दर्द समझते हैं।', en: "Sad shayari captures what's hard to say out loud. Ghalib's restlessness, Faiz's longing, Jaun Elia's ache — this collection holds some of the finest verses on sorrow and loneliness. Reach for these when words feel heavy, when you want to sit with a feeling rather than fix it, or to let a friend know their pain is understood." },
  romantic:     { hi: 'रोमांटिक शायरी प्यार की मीठी अनुभूति है। गुलज़ार, साहिर लुधियानवी और जावेद अख़्तर की नरम शोख़ शायरियाँ जो आपके दिल को छू जाएंगी। यह श्रेणी लव शायरी से थोड़ी हल्की और चंचल है — डेटिंग के शुरुआती दिनों, हल्के-फुल्के फ़्लर्ट या रोज़मर्रा के रोमांस के लिए बेहतर है।', en: "Romantic shayari is love's lighter, playful side. Gentle, warm verses by Gulzar, Sahir Ludhianvi, and Javed Akhtar that lean more mischievous than intense — well suited to the early days of dating, a flirty text, or everyday affection rather than heavier declarations." },
  hearttouching:{ hi: 'दिल को छू लेने वाली शायरियाँ ज़िंदगी के सच को बयाँ करती हैं। इनमें इंसानियत, रिश्ते और जज़्बात की गहरी बात है — जो पढ़ने वाले के दिल में उतर जाती है। यह श्रेणी सिर्फ़ प्रेम तक सीमित नहीं — माँ-बाप, परिवार, इंसानियत और ज़िंदगी के छोटे-बड़े लम्हों पर लिखी गई शायरियाँ यहाँ मिलेंगी, जो किसी को भी भावुक कर दें।', en: 'Heart-touching shayari speaks to life\'s deeper truths. These verses explore humanity, relationships, and emotion in ways that stay with the reader. Unlike love-focused categories, this one ranges across parents, family, and everyday moments — the kind of shayari that quietly moves whoever reads it.' },
  attitude:     { hi: 'एटीट्यूड शायरी आत्मविश्वास और ख़ुद्दारी की बात करती है। इस संग्रह में सबसे दमदार शायरियाँ हैं — WhatsApp status और Instagram bio के लिए परफेक्ट। ये उन पलों के लिए हैं जब आपको ख़ुद पर भरोसा जताना हो, किसी आलोचना का जवाब बिना झगड़े के देना हो, या बस अपनी शर्तों पर जीने की बात कहनी हो।', en: 'Attitude shayari is about confidence and self-respect. This is the boldest, sharpest collection here — well suited for a WhatsApp status or Instagram bio, or for the moments when you want to answer criticism without an argument, or simply state that you live life on your own terms.' },
  friendship:   { hi: 'दोस्ती की शायरी यारी और दोस्तों के प्यार को बयाँ करती है। इस संग्रह में दोस्तों के लिए बेहतरीन शायरियाँ हैं जो आप WhatsApp पर भेज सकते हैं। दोस्त के जन्मदिन, Friendship Day, या बस यह बताने के लिए कि आप उनके साथ के लिए शुक्रगुज़ार हैं — यहाँ हर मौक़े की शायरी है।', en: "Friendship shayari gives voice to the bonds we build outside family. This collection is well suited for a friend's birthday, Friendship Day, or simply letting someone know you're grateful for their company — light-hearted verses about loyalty, banter, and being there for each other." },
  motivational: { hi: 'प्रेरणादायक शायरी हौसला देती है और आगे बढ़ने की ताकत देती है। इक़बाल, दिनकर और रहत इन्दौरी की रचनाओं से भरपूर यह संग्रह हर मुश्किल में राह दिखाएगा। परीक्षा से पहले, किसी असफलता के बाद, या बस सुबह की शुरुआत के लिए — ये शायरियाँ हिम्मत जगाने के लिए हैं।', en: 'Motivational shayari is meant to steady you before a hard day and lift you after a setback. Drawing on Allama Iqbal, Ramdhari Singh Dinkar, and Rahat Indori, this collection works well before an exam, after a failure, or as a way to start the morning with resolve rather than dread.' },
  life:         { hi: 'ज़िंदगी की शायरी अनुभव और एहसास की बात करती है। यहाँ मिलेंगी वो शायरियाँ जो ज़िंदगी के हर मोड़ पर आपके साथ खड़ी रहें। दार्शनिक अंदाज़ में लिखी ये शायरियाँ उन लोगों के लिए हैं जो ज़िंदगी के उतार-चढ़ाव पर सोचना, ठहरकर समझना पसंद करते हैं।', en: 'Life shayari reflects on experience itself — the ups and downs, the waiting, the small wisdoms that only come with time. Written in a more philosophical register, these verses suit readers who like to pause and reflect rather than simply move through the day.' },
  ghazal:       { hi: 'ग़ज़ल उर्दू-हिंदी काव्य की सबसे नाज़ुक और मक़बूल विधा है। मिर्ज़ा ग़ालिब, फ़ैज़ अहमद फ़ैज़, मीर तक़ी मीर और बशीर बद्र की बेहतरीन ग़ज़लें। हर शेर अपने आप में मुकम्मल विचार रखता है — इसलिए ग़ज़ल पढ़ने वालों के लिए, महफ़िल में सुनाने के लिए, या शायरी की क्लासिकी परंपरा को समझने के लिए यह संग्रह उपयुक्त है।', en: 'Ghazal is the most refined and enduring form in Urdu-Hindi poetry — a series of independent couplets bound by rhyme and refrain. This collection of works by Mirza Ghalib, Faiz Ahmed Faiz, Mir Taqi Mir, and Bashir Badr suits readers who want to understand the classical tradition, recite verses aloud, or savor a single sher at a time.' },
  dohe:         { hi: 'दोहे हिंदी साहित्य की वो धरोहर हैं जो सदियों से जीवन का सच सिखाती आई हैं। कबीर दास और रहीम दास के प्रसिद्ध दोहे हिंदी अर्थ सहित। दो पंक्तियों में गहरी नैतिक सीख छुपी होती है — इन्हें अक्सर सिखाने, समझाने या किसी बहस में सटीक बात कहने के लिए उद्धृत किया जाता है।', en: 'Dohe are a centuries-old form of two-line couplets carrying practical, moral wisdom — most famously from Kabir Das and Rahim Das, given here with meaning in both Hindi and English. They are often quoted while teaching, counseling, or making a sharp point in conversation.' },
  patriotic:    { hi: 'देशभक्ति शायरी में जोश, जज़्बा और वतन की मोहब्बत होती है। दिनकर, इक़बाल और सुभद्रा कुमारी चौहान की देशभक्ति रचनाएं। स्वतंत्रता दिवस, गणतंत्र दिवस या किसी भी ऐसे मौक़े के लिए जब देश के प्रति सम्मान जताना हो, ये शायरियाँ उपयुक्त हैं।', en: 'Patriotic shayari carries the fervor and pride of love for one\'s nation, drawing on Ramdhari Singh Dinkar, Allama Iqbal, and Subhadra Kumari Chauhan. These verses suit Independence Day, Republic Day, or any occasion calling for a tribute to the country.' },
  nature:       { hi: 'क़ुदरत की शायरी में पहाड़, नदियाँ, बादल और मौसम की ख़ूबसूरती होती है। यहाँ मिलेंगी प्रकृति की सुंदरता को शब्दों में बयाँ करने वाली बेहतरीन शायरियाँ। मानसून, यात्रा की तस्वीरों के कैप्शन, या बस प्रकृति के प्रति प्रेम जताने के लिए यह संग्रह उपयुक्त है।', en: 'Nature shayari captures the beauty of mountains, rivers, clouds, and changing seasons in verse. This collection works well for monsoon posts, travel photo captions, or simply expressing a love for the natural world.' },
  twolines:     { hi: 'दो लाइन की शायरी में बड़ी बात छुपी होती है। ये शेर कम शब्दों में दिल की पूरी बात कह देते हैं — WhatsApp status और Instagram bio के लिए परफेक्ट। जब आपको संक्षेप में, बिना लंबी शायरी पढ़े-सुनाए अपनी बात कहनी हो, तब यही श्रेणी सबसे तेज़ काम आती है।', en: 'Two-line shayari proves that great poetry does not need many words — a complete thought captured in a single couplet, well suited for a WhatsApp status or Instagram bio when you want to say something quickly without a longer verse.' },
  miss:         { hi: 'याद की शायरी उन लम्हों को जीवित रखती है जो गुज़र गए। किसी अपने की याद, दूरियाँ, इंतज़ार — इस संग्रह में वो सब है जो दिल में अटका रहता है। दूर रहते किसी अज़ीज़ को यह एहसास दिलाने के लिए कि वो याद आते हैं, यह श्रेणी सबसे उपयुक्त है।', en: 'Miss-you shayari keeps alive the moments that have passed — memories of someone dear, the ache of distance, the wait for their return. This category is well suited for letting someone far away know they are missed.' },
  breakup:      { hi: 'जुदाई की शायरी टूटे हुए दिल की आवाज़ है। ब्रेकअप के दर्द और अलविदा की शायरियाँ — यहाँ मिलेगा सब कुछ जो दिल कहना चाहे। रिश्ता ख़त्म होने के बाद के दर्द को समझने, या उससे उबरने की शुरुआत करने के लिए ये शायरियाँ पढ़ी जा सकती हैं।', en: 'Breakup shayari gives voice to a heart in the aftermath of an ending. These verses on heartbreak, separation, and farewell can help make sense of that pain, or mark the first step toward moving on.' },
  nazm:         { hi: 'नज़्म हिंदी-उर्दू की वो कविता है जो एक कहानी कहती है। फ़ैज़, इक़बाल, गुलज़ार और साहिर की बेहतरीन नज़्में — जो दिल में घर कर जाती हैं। ग़ज़ल के उलट, नज़्म में एक ही विचार शुरू से अंत तक चलता है — इसलिए इत्मीनान से, पूरी बैठकर पढ़ने वालों के लिए यह श्रेणी उपयुक्त है।', en: 'Nazm is a Hindi-Urdu poetic form that tells a single, continuous story or idea from start to finish — unlike the ghazal\'s independent couplets. Drawing on Faiz Ahmed Faiz, Allama Iqbal, Gulzar, and Sahir Ludhianvi, this collection rewards a slower, more attentive read.' },
};

// Category FAQ — templated from real per-category data (label, hindi/urdu
// name, live count), not generic boilerplate repeated verbatim across pages.
const buildCategoryFAQ = (cat, count, language) => {
  const nativeName = language === 'ur' ? cat.urdu : cat.hindi;
  if (language === 'ur' || language === 'hi') {
    return [
      { q: `${cat.hindi} शायरी क्या है?`, a: `${cat.hindi} (${cat.label}) शायरी इस थीम पर लिखी गई शायरियों का संग्रह है। इस समय इस श्रेणी में ${count} शायरियाँ हिंदी, उर्दू और अंग्रेज़ी में उपलब्ध हैं।` },
      { q: `क्या मैं ये शायरियाँ WhatsApp और Instagram पर शेयर कर सकता/सकती हूँ?`, a: 'हाँ, व्यक्तिगत उपयोग के लिए इन्हें WhatsApp status, Instagram bio/story या किसी और सोशल मीडिया पर उचित श्रेय के साथ शेयर किया जा सकता है। पूरी शर्तें हमारी नियम एवं शर्तें पृष्ठ पर देखें।' },
      { q: `${nativeName} शायरी उर्दू और अंग्रेज़ी में भी मिलेगी?`, a: 'हाँ, इस श्रेणी की हर शायरी मूल हिंदी/उर्दू लिपि के साथ-साथ अंग्रेज़ी अनुवाद में भी उपलब्ध है — भाषा ऊपर नेविगेशन से बदलें।' },
    ];
  }
  return [
    { q: `What is ${cat.label} shayari?`, a: `${cat.label} (${cat.hindi}) shayari is a themed collection of Hindi-Urdu poetry. This category currently holds ${count} shayaris, available in Hindi, Urdu, and English.` },
    { q: `Can I share these shayaris on WhatsApp and Instagram?`, a: 'Yes — for personal use, these can be shared as a WhatsApp status, Instagram bio/story, or on other social platforms with proper credit. See our Terms & Conditions for the full policy.' },
    { q: `Is ${cat.label} shayari available in Urdu and English too?`, a: 'Yes, every shayari in this category is shown in its original Hindi/Urdu script along with an English translation — switch language from the navigation bar.' },
  ];
};

// Grid with in-feed ad after every N cards
const ShayariGridWithAds = ({ shayaris, adEvery = 8 }) => {
  const items = [];
  shayaris.forEach((s, i) => {
    items.push(<ShayariCard key={s.id} shayari={s} />);
    if ((i + 1) % adEvery === 0 && i + 1 < shayaris.length) {
      items.push(
        <div key={`ad-${i}`} className="shayari-grid__ad-slot">
          <AdBanner type="infeed" label={false} />
        </div>
      );
    }
  });
  return <div className="shayari-grid">{items}</div>;
};

const CategoryPage = () => {
  const { categoryId } = useParams();
  const { language } = useApp();
  const [sortBy, setSortBy] = useState('popular');

  const T = {
    hi: { notFound:'श्रेणी नहीं मिली', goHome:'← मुख्य पृष्ठ', shayari:'शायरी', inCollection:'शायरियाँ इस संग्रह में', popular:'🔥 लोकप्रिय', featured:'⭐ चुनिंदा', all:'📚 सभी', noShayari:(s)=>`कोई ${s} शायरी नहीं मिली`, showAll:'सभी दिखाएँ' },
    ur: { notFound:'زمرہ نہیں ملا',     goHome:'← مرکزی صفحہ', shayari:'شاعری', inCollection:'شاعریاں اس مجموعے میں', popular:'🔥 مقبول', featured:'⭐ منتخب', all:'📚 سب', noShayari:(s)=>`کوئی ${s} شاعری نہیں ملی`, showAll:'سب دکھائیں' },
    en: { notFound:'Category not found', goHome:'← Go Home',     shayari:'Shayari', inCollection:'Shayaris in this collection', popular:'🔥 Popular', featured:'⭐ Featured', all:'📚 All', noShayari:(s)=>`No ${s} shayari found`, showAll:'Show All' },
    es: { notFound:'Categoría no encontrada', goHome:'← Inicio', shayari:'Shayari', inCollection:'Shayaris en esta colección', popular:'🔥 Popular', featured:'⭐ Destacado', all:'📚 Todos', noShayari:(s)=>`No hay shayari de ${s}`, showAll:'Mostrar todo' },
    fr: { notFound:'Catégorie introuvable',   goHome:'← Accueil', shayari:'Shayari', inCollection:'Shayaris dans cette collection', popular:'🔥 Populaire', featured:'⭐ À la une', all:'📚 Tous', noShayari:(s)=>`Aucun shayari ${s}`, showAll:'Tout afficher' },
  };
  const t = T[language] || T.hi;

  const cat = CATEGORIES.find(c => c.id === categoryId);

  const shayaris = useMemo(() => {
    let list = SHAYARI.filter(s => s.category === categoryId);
    if (sortBy === 'popular') return [...list].sort((a, b) => b.likes - a.likes);
    if (sortBy === 'featured') return list.filter(s => s.featured);
    return list;
  }, [categoryId, sortBy]);

  useSEO({
    title: cat
      ? `${cat.label} Shayari — ${cat.hindi} | Best ${cat.label} Hindi Urdu Poetry`
      : 'Category — शायरी',
    description: cat
      ? `Read the best ${cat.label} shayari in Hindi, Urdu and English. ${shayaris.length} ${cat.label} shayaris — ${cat.hindi} (${cat.urdu}). Share on WhatsApp & Instagram.`
      : '',
    keywords: cat
      ? [
          `${cat.label} shayari`, `${cat.hindi} shayari`, `${cat.urdu} shayari`,
          `best ${cat.label} shayari`, `${cat.label} poetry in hindi`, `${cat.label} poetry in urdu`,
          `hindi ${cat.label} shayari`, `urdu ${cat.label} shayari`,
          `${cat.label} shayari 2024`, `${cat.label} shayari download`,
          `whatsapp ${cat.label} shayari`, `instagram ${cat.label} shayari`,
          `2 line ${cat.label} shayari`, `heart touching ${cat.label} shayari`,
          `${cat.label} shayari in english`, `new ${cat.label} shayari`,
          'shayaristudio', 'hindi shayari', 'urdu shayari',
        ].join(', ')
      : '',
    canonicalPath: `/category/${categoryId}`,
    ogLocale: 'hi_IN',
    hreflang: cat ? [
      { lang: 'hi',        href: `https://shayaristudio.online/category/${categoryId}` },
      { lang: 'ur',        href: `https://shayaristudio.online/category/${categoryId}` },
      { lang: 'en',        href: `https://shayaristudio.online/category/${categoryId}` },
      { lang: 'es',        href: `https://shayaristudio.online/category/${categoryId}` },
      { lang: 'fr',        href: `https://shayaristudio.online/category/${categoryId}` },
      { lang: 'x-default', href: `https://shayaristudio.online/category/${categoryId}` },
    ] : undefined,
    structuredData: cat ? {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'CollectionPage',
          '@id': `https://shayaristudio.online/category/${categoryId}#collection`,
          url: `https://shayaristudio.online/category/${categoryId}`,
          name: `${cat.label} Shayari — ${cat.hindi} | ShayariStudio`,
          description: `${shayaris.length} best ${cat.label} shayari in Hindi, Urdu and English — ${cat.hindi} (${cat.urdu}). Share on WhatsApp, Instagram.`,
          numberOfItems: shayaris.length,
          inLanguage: ['hi', 'ur', 'en'],
          breadcrumb: {
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://shayaristudio.online/' },
              { '@type': 'ListItem', position: 2, name: `${cat.label} Shayari`, item: `https://shayaristudio.online/category/${categoryId}` },
            ],
          },
        },
        {
          '@type': 'ItemList',
          name: `Top ${cat.label} Shayari`,
          itemListElement: [...shayaris].sort((a, b) => b.likes - a.likes).slice(0, 10).map((s, i) => ({
            '@type': 'ListItem',
            position: i + 1,
            item: {
              '@type': 'CreativeWork',
              name: s.author?.en ? `${s.author.en} — ${cat.label} Shayari` : `${cat.label} Shayari`,
              text: s.hi || s.en,
              inLanguage: 'hi',
              author: s.author?.en ? { '@type': 'Person', name: s.author.en } : undefined,
              url: `https://shayaristudio.online/shayari/${s.id}`,
            },
          })),
        },
        {
          '@type': 'FAQPage',
          mainEntity: buildCategoryFAQ(cat, shayaris.length, 'en').map(item => ({
            '@type': 'Question',
            name: item.q,
            acceptedAnswer: { '@type': 'Answer', text: item.a },
          })),
        },
      ],
    } : undefined,
  });

  if (!cat) {
    return (
      <div className="not-found">
        <h2>{t.notFound}</h2>
        <Link to="/" className="btn-primary">{t.goHome}</Link>
      </div>
    );
  }

  return (
    <main className="main-layout">
      <CategoryStrip />

      {/* ── Category Banner ─────────────────────────────────────── */}
      <section className="cat-banner" style={{ '--cat-gradient': cat.gradient }}>
        <div className="cat-banner__inner">
          <div style={{marginBottom:'.5rem',opacity:.8}}><Logo size="sm" variant="white" /></div>
          <div className="cat-banner__icon">{cat.icon}</div>
          <div className="cat-banner__text">
            <h1 className="cat-banner__title">
              {language === 'ur' ? cat.urdu : language === 'hi' ? cat.hindi : cat.label} {t.shayari}
            </h1>
            <p className="cat-banner__native">{cat.hindi} • {cat.urdu}</p>
            <p className="cat-banner__count">{shayaris.length} {t.inCollection}</p>
          </div>
        </div>
        <div className="cat-banner__waves">
          <div className="wave wave1" />
          <div className="wave wave2" />
        </div>
      </section>

      <div className="container">

        {/* Search from category page */}
        <div style={{margin:'1.5rem 0',display:'flex',justifyContent:'center'}}>
          <GlobalSearch compact />
        </div>

        {/* ── Category description for SEO & readers ────────────── */}
        {CATEGORY_DESCRIPTIONS[categoryId] && (
          <div style={{
            background:'var(--bg-card)',
            border:'1px solid var(--border)',
            borderRadius:'12px',
            padding:'1rem 1.25rem',
            marginBottom:'1.5rem',
            lineHeight:1.7,
          }}>
            <p style={{margin:'0 0 .4rem',fontSize:'.95rem',color:'var(--text-primary)'}}>
              {CATEGORY_DESCRIPTIONS[categoryId].hi}
            </p>
            <p style={{margin:0,fontSize:'.88rem',color:'var(--text-muted)'}}>
              {CATEGORY_DESCRIPTIONS[categoryId].en}
            </p>
          </div>
        )}

        {/* ── Ad (single placement, after real content) ───────────── */}
        <div className="ad-section ad-section--top">
          <AdBanner type="leaderboard" adSlot="1111111111" />
        </div>

        <div className="section-header">
          <h2 className="section-title">
            {cat.icon} {cat.label}
            <span style={{ color: 'var(--text-muted)', fontSize: '1rem', marginLeft: '0.5rem' }}>
              ({language === 'ur' ? cat.urdu : language === 'hi' ? cat.hindi : cat.label})
            </span>
          </h2>
          <div className="sort-tabs">
            {[
              { id: 'popular',  label: t.popular  },
              { id: 'featured', label: t.featured },
              { id: 'all',      label: t.all      },
            ].map(tab => (
              <button
                key={tab.id}
                className={`sort-tab ${sortBy === tab.id ? 'sort-tab--active' : ''}`}
                onClick={() => setSortBy(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <div className="content-grid">
          <div className="content-main">
            {shayaris.length === 0 ? (
              <div className="empty-state">
                <div className="empty-state__icon">{cat.icon}</div>
                <h3>{t.noShayari(sortBy)}</h3>
                <button onClick={() => setSortBy('all')} className="btn-primary">{t.showAll}</button>
              </div>
            ) : (
              <ShayariGridWithAds shayaris={shayaris} adEvery={8} />
            )}
          </div>
          <Sidebar />
        </div>

        {/* ── Bottom ad (single placement) ─────────────────────────── */}
        <div className="ad-section ad-section--bottom">
          <AdBanner type="leaderboard" adSlot="1111111111" />
        </div>

        {/* ── Category FAQ ─────────────────────────────────────────── */}
        <FAQAccordion
          title={language === 'ur' ? '❓ اکثر پوچھے گئے سوالات' : language === 'hi' ? '❓ अक्सर पूछे जाने वाले सवाल' : '❓ Frequently Asked Questions'}
          items={buildCategoryFAQ(cat, shayaris.length, language)}
        />

        {/* ── More Categories ─────────────────────────────────────── */}
        <section style={{marginTop:'2.5rem',marginBottom:'2rem'}}>
          <h2 style={{fontSize:'1.1rem',fontWeight:700,marginBottom:'1rem',color:'var(--text-primary)'}}>
            और श्रेणियाँ खोजें &nbsp;|&nbsp; Explore More Categories
          </h2>
          <div style={{
            display:'grid',
            gridTemplateColumns:'repeat(auto-fill,minmax(150px,1fr))',
            gap:'.6rem',
          }}>
            {CATEGORIES.filter(c => c.id !== categoryId).map(c => (
              <Link
                key={c.id}
                to={`/category/${c.id}`}
                style={{
                  display:'flex',
                  flexDirection:'column',
                  alignItems:'center',
                  gap:'.2rem',
                  padding:'.65rem .5rem',
                  background:'var(--bg-card)',
                  border:'1px solid var(--border)',
                  borderRadius:'10px',
                  textDecoration:'none',
                  transition:'border-color .2s,transform .15s',
                }}
                onMouseEnter={e=>{e.currentTarget.style.borderColor='var(--accent)';e.currentTarget.style.transform='translateY(-2px)';}}
                onMouseLeave={e=>{e.currentTarget.style.borderColor='var(--border)';e.currentTarget.style.transform='none';}}
              >
                <span style={{fontSize:'1.4rem'}}>{c.icon}</span>
                <span style={{fontSize:'.8rem',fontWeight:600,color:'var(--text-primary)',textAlign:'center'}}>{c.label}</span>
                <span style={{fontSize:'.72rem',color:'var(--text-muted)'}}>{c.hindi}</span>
              </Link>
            ))}
          </div>
        </section>

      </div>
    </main>
  );
};

export default CategoryPage;
